import classNames from 'classnames';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useEffect, useContext } from 'react';
import React from 'react';

import ConfigContext from '../service/context/config';
import ConfigRentContext from '../service/context/config-rent';
import ConfigVosContext from '../service/context/config-vos';
import type { IAddress } from '../types';
import { DEFAULT_DELIVERY_COUNTRY } from '../utils/constants';
import { isEmail } from '../utils/validate';

import Input from './ui/Input';
import './ui/Input.scss';
import PhoneInput from './ui/PhoneInput';
import Select from './ui/Select';

interface IAddressProps {
  deliveryCountries?: string[];
  address?: IAddress;
  showFax?: boolean;
  showCustomerNumber?: boolean;
  showCountrySelect?: boolean;
  customerNumber?: string;
  showReferenceNumber?: boolean;
  showError?: boolean;
  referenceNumber?: string;
  referenceNumberLabel?: string;
  propertyKey: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  disabled?: boolean;
  hideName?: boolean;
  emailLabel?: string;
}

const Address: FC<IAddressProps> = ({
  propertyKey,
  customerNumber = '',
  showCustomerNumber = false,
  showCountrySelect = false,
  referenceNumber = '',
  showReferenceNumber = false,
  referenceNumberLabel = 'Referenznummer',
  showFax = false,
  disabled = false,
  showError = false,
  hideName = false,
  emailLabel = 'E-Mail',
  deliveryCountries,
  address,
  onChange
}) => {
  const appContext = useContext(ConfigContext);
  const appContextVos = useContext(ConfigVosContext);
  const appContextRent = useContext(ConfigRentContext);
  const {
    delivery_countries: deliveryCountriesServiceForm,
    delivery_countries_mapping: countryMapping
  } = appContext || appContextVos || appContextRent || {};
  const {
    country,
    firstname,
    lastname,
    company,
    email,
    street,
    house_number,
    salutation,
    zip,
    city,
    phone,
    mobile,
    fax
  } = address || {};
  const deliverCountriesToUse = useMemo(() => {
    return deliveryCountries || deliveryCountriesServiceForm || [ DEFAULT_DELIVERY_COUNTRY ];
  }, [ deliveryCountries, deliveryCountriesServiceForm ]);

  useEffect(() => {
    if (onChange && countryMapping && country && countryMapping[country]) {
      onChange({
        target: {
          value: countryMapping[country],
          name: `${propertyKey}.country_code`
        } as unknown as EventTarget
      } as unknown as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>);
    }
  }, [ country, countryMapping, onChange, propertyKey ]);

  return (
    <div className="form-grid">
      <div>
        {showCustomerNumber && (
          <Input
            label="Kundenummer (falls vorhanden)"
            value={customerNumber}
            name="customer_number"
            onChange={onChange}
            disabled={disabled}
          />
        )}
        <Input
          label="Firmenname"
          value={company}
          name={`${propertyKey}.company`}
          onChange={onChange}
          disabled={disabled}
          wrapperClass={classNames({
            'has-error': showError && !company
          })}
        />
        {!hideName && (
          <div className="styled-input">
            <label>Ansprechpartner Vor- & Nachname</label>
            <div className="styled-input-wrap">
              <Select
                options={[ '', 'Herr', 'Frau' ]}
                value={salutation}
                name={`${propertyKey}.salutation`}
                onChange={onChange}
                disabled={disabled}
                wrapperClass={classNames('max-w', {
                  'has-error': showError && !salutation
                })}
                required
              />
              <Input
                value={firstname}
                name={`${propertyKey}.firstname`}
                onChange={onChange}
                disabled={disabled}
                wrapperClass={classNames({
                  'has-error': showError && !firstname
                })}
              />
              <Input
                value={lastname}
                name={`${propertyKey}.lastname`}
                onChange={onChange}
                disabled={disabled}
                wrapperClass={classNames({
                  'has-error': showError && !lastname
                })}
              />
            </div>
          </div>
        )}
        <Input
          type="email"
          label={emailLabel}
          value={email}
          name={`${propertyKey}.email`}
          onChange={onChange}
          disabled={disabled}
          wrapperClass={classNames({
            'has-error': showError && (!email || !isEmail(email))
          })}
        />
        <PhoneInput
          label="Festnetz"
          value={phone}
          name={`${propertyKey}.phone`}
          country={country}
          required
          showError={showError}
          onChange={onChange}
          disabled={disabled}
        />
      </div>
      <div>
        {showCountrySelect && deliverCountriesToUse.length && (
          <Select
            options={[ '', ...deliverCountriesToUse ]}
            label="Land"
            value={country}
            name={`${propertyKey}.country`}
            onChange={onChange}
            disabled={disabled}
            wrapperClass={classNames({
              'has-error': showError && !country
            })}
          />
        )}
        <div className="styled-input">
          <label>Straße & Hausnummer</label>
          <div className="styled-input-wrap">
            <Input
              value={street}
              name={`${propertyKey}.street`}
              onChange={onChange}
              disabled={disabled}
              wrapperClass={classNames({
                'has-error': showError && !street
              })}
            />
            <Input
              value={house_number}
              name={`${propertyKey}.house_number`}
              onChange={onChange}
              disabled={disabled}
              wrapperClass={classNames({
                'has-error': showError && !house_number
              })}
              isSmall
            />
          </div>
        </div>
        <div className="styled-input">
          <label>Postleitzahl & Ort</label>
          <div className="styled-input-wrap">
            <Input
              value={zip}
              name={`${propertyKey}.zip`}
              onChange={onChange}
              disabled={disabled}
              wrapperClass={classNames({
                'has-error': showError && !zip
              })}
              isSmall
            />
            <Input
              value={city}
              name={`${propertyKey}.city`}
              onChange={onChange}
              disabled={disabled}
              wrapperClass={classNames({
                'has-error': showError && !city
              })}
            />
          </div>
        </div>
        {showReferenceNumber && (
          <Input
            label={referenceNumberLabel}
            value={referenceNumber}
            disabled={disabled}
            name="reference_number"
            onChange={onChange}
          />
        )}
        <PhoneInput
          label="Mobilnummer (optional)"
          value={mobile}
          name={`${propertyKey}.mobile`}
          country={country}
          showError={showError}
          onChange={onChange}
          disabled={disabled}
        />
        {showFax && (
          <Input
            type="phone"
            label="FAX"
            value={fax}
            disabled={disabled}
            name={`${propertyKey}.fax`}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  );
};

export default Address;
